import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

interface Worksheet {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  pages: number;
  questions: number;
  downloadUrl: string;
}

const worksheets: Worksheet[] = [
  // Punctuation (7 worksheets)
  {
    id: 'act-eng-punct-1',
    title: 'Commas with Essential vs. Non-Essential Elements',
    description: 'Master comma usage with restrictive and non-restrictive clauses, appositives, and parenthetical elements',
    category: 'Punctuation',
    difficulty: 'Intermediate',
    pages: 4,
    questions: 25,
    downloadUrl: 'https://public.readdy.ai/ai/img_res/3e88505b3b3aa7c507e5a72479a946af.jpg'
  },
  {
    id: 'act-eng-punct-2',
    title: 'Commas in Series and Lists',
    description: 'Practice proper comma placement in series, lists, and coordinate adjectives',
    category: 'Punctuation',
    difficulty: 'Beginner',
    pages: 3,
    questions: 20,
    downloadUrl: 'https://readdy.ai/api/search-image?query=educational%20worksheet%20design%20featuring%20comma%20usage%20in%20lists%20and%20series%20with%20multiple%20items%20connected%20by%20commas%20clean%20academic%20layout%20white%20background%20emerald%20green%20accents%20professional%20typography&width=400&height=300&seq=act-eng-punct-2&orientation=landscape'
  },
  {
    id: 'act-eng-punct-3',
    title: 'Semicolons and Colons',
    description: 'Learn when to use semicolons to join independent clauses and colons to introduce lists or explanations',
    category: 'Punctuation',
    difficulty: 'Intermediate',
    pages: 4,
    questions: 25,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20worksheet%20header%20showing%20semicolon%20and%20colon%20punctuation%20marks%20with%20sentence%20examples%20and%20usage%20rules%20clean%20minimalist%20design%20white%20background%20teal%20color%20scheme%20academic%20style&width=400&height=300&seq=act-eng-punct-3&orientation=landscape'
  },
  {
    id: 'act-eng-punct-4',
    title: 'Apostrophes for Possession and Contractions',
    description: 'Master apostrophe usage in possessive nouns, contractions, and common errors to avoid',
    category: 'Punctuation',
    difficulty: 'Beginner',
    pages: 3,
    questions: 20,
    downloadUrl: 'https://readdy.ai/api/search-image?query=educational%20worksheet%20design%20featuring%20apostrophe%20punctuation%20with%20possessive%20nouns%20and%20contractions%20examples%20clean%20academic%20layout%20white%20background%20emerald%20and%20teal%20accents%20professional%20style&width=400&height=300&seq=act-eng-punct-4&orientation=landscape'
  },
  {
    id: 'act-eng-punct-5',
    title: 'Dashes and Parentheses',
    description: 'Practice using em dashes and parentheses to set off non-essential information effectively',
    category: 'Punctuation',
    difficulty: 'Intermediate',
    pages: 4,
    questions: 25,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20worksheet%20header%20showing%20em%20dashes%20and%20parentheses%20in%20sentences%20with%20non-essential%20information%20highlighted%20clean%20minimalist%20academic%20design%20white%20background%20teal%20accents&width=400&height=300&seq=act-eng-punct-5&orientation=landscape'
  },
  {
    id: 'act-eng-punct-6',
    title: 'End-of-Sentence Punctuation',
    description: 'Master periods, question marks, and exclamation points in various sentence contexts',
    category: 'Punctuation',
    difficulty: 'Beginner',
    pages: 3,
    questions: 20,
    downloadUrl: 'https://readdy.ai/api/search-image?query=educational%20worksheet%20design%20featuring%20period%20question%20mark%20and%20exclamation%20point%20with%20sentence%20examples%20clean%20academic%20layout%20white%20background%20emerald%20green%20color%20scheme&width=400&height=300&seq=act-eng-punct-6&orientation=landscape'
  },
  {
    id: 'act-eng-punct-review',
    title: 'Comprehensive Punctuation Review',
    description: 'Mixed practice covering all punctuation rules tested on the ACT English section',
    category: 'Punctuation',
    difficulty: 'Advanced',
    pages: 6,
    questions: 40,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20comprehensive%20review%20worksheet%20header%20showing%20multiple%20punctuation%20marks%20including%20commas%20semicolons%20colons%20apostrophes%20dashes%20clean%20academic%20design%20white%20background%20teal%20and%20emerald%20gradient&width=400&height=300&seq=act-eng-punct-review&orientation=landscape'
  },

  // Grammar & Usage (8 worksheets)
  {
    id: 'act-eng-grammar-1',
    title: 'Subject-Verb Agreement',
    description: 'Practice matching subjects with verbs in number, including tricky cases with intervening phrases',
    category: 'Grammar & Usage',
    difficulty: 'Intermediate',
    pages: 4,
    questions: 30,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20educational%20worksheet%20design%20showing%20subject%20verb%20agreement%20with%20singular%20and%20plural%20subjects%20connected%20to%20matching%20verbs%20clean%20academic%20style%20white%20background%20emerald%20accents&width=400&height=300&seq=act-eng-grammar-1&orientation=landscape'
  },
  {
    id: 'act-eng-grammar-2',
    title: 'Pronoun-Antecedent Agreement',
    description: 'Master pronoun agreement in number, gender, and person with their antecedents',
    category: 'Grammar & Usage',
    difficulty: 'Intermediate',
    pages: 4,
    questions: 30,
    downloadUrl: 'https://readdy.ai/api/search-image?query=educational%20worksheet%20header%20featuring%20pronouns%20connected%20to%20their%20antecedents%20with%20arrows%20showing%20agreement%20clean%20minimalist%20design%20white%20background%20teal%20and%20emerald%20color%20scheme&width=400&height=300&seq=act-eng-grammar-2&orientation=landscape'
  },
  {
    id: 'act-eng-grammar-3',
    title: 'Verb Tense and Formation',
    description: 'Practice maintaining consistent verb tense and using correct verb forms throughout passages',
    category: 'Grammar & Usage',
    difficulty: 'Intermediate',
    pages: 5,
    questions: 35,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20worksheet%20design%20showing%20verb%20tenses%20timeline%20with%20past%20present%20and%20future%20forms%20clean%20academic%20layout%20white%20background%20emerald%20green%20accents%20educational%20style&width=400&height=300&seq=act-eng-grammar-3&orientation=landscape'
  },
  {
    id: 'act-eng-grammar-4',
    title: 'Pronoun Case',
    description: 'Learn when to use subjective, objective, and possessive pronoun cases correctly',
    category: 'Grammar & Usage',
    difficulty: 'Intermediate',
    pages: 4,
    questions: 30,
    downloadUrl: 'https://readdy.ai/api/search-image?query=educational%20worksheet%20header%20featuring%20pronoun%20cases%20with%20I%20me%20my%20examples%20in%20different%20sentence%20positions%20clean%20minimalist%20academic%20design%20white%20background%20teal%20accents&width=400&height=300&seq=act-eng-grammar-4&orientation=landscape'
  },
  {
    id: 'act-eng-grammar-5',
    title: 'Adjectives, Adverbs, and Modifiers',
    description: 'Practice correct modifier placement and choosing between adjectives and adverbs',
    category: 'Grammar & Usage',
    difficulty: 'Intermediate',
    pages: 4,
    questions: 30,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20worksheet%20design%20showing%20adjectives%20and%20adverbs%20modifying%20nouns%20and%20verbs%20with%20arrows%20and%20labels%20clean%20academic%20style%20white%20background%20emerald%20and%20teal%20color%20scheme&width=400&height=300&seq=act-eng-grammar-5&orientation=landscape'
  },
  {
    id: 'act-eng-grammar-6',
    title: 'Comparative and Superlative Forms',
    description: 'Master the correct use of comparative and superlative adjectives and adverbs',
    category: 'Grammar & Usage',
    difficulty: 'Beginner',
    pages: 3,
    questions: 25,
    downloadUrl: 'https://readdy.ai/api/search-image?query=educational%20worksheet%20header%20featuring%20comparative%20and%20superlative%20forms%20with%20good%20better%20best%20examples%20clean%20minimalist%20design%20white%20background%20emerald%20green%20accents&width=400&height=300&seq=act-eng-grammar-6&orientation=landscape'
  },
  {
    id: 'act-eng-grammar-7',
    title: 'Idiomatic Usage and Prepositions',
    description: 'Practice standard English idioms and correct preposition usage in common phrases',
    category: 'Grammar & Usage',
    difficulty: 'Advanced',
    pages: 4,
    questions: 30,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20worksheet%20design%20showing%20idiomatic%20expressions%20and%20prepositions%20in%20context%20with%20common%20phrases%20clean%20academic%20layout%20white%20background%20teal%20and%20emerald%20accents&width=400&height=300&seq=act-eng-grammar-7&orientation=landscape'
  },
  {
    id: 'act-eng-grammar-review',
    title: 'Comprehensive Grammar and Usage Review',
    description: 'Mixed practice covering all grammar and usage rules tested on the ACT English section',
    category: 'Grammar & Usage',
    difficulty: 'Advanced',
    pages: 6,
    questions: 45,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20comprehensive%20grammar%20review%20worksheet%20header%20with%20multiple%20grammar%20concepts%20including%20verbs%20pronouns%20modifiers%20clean%20academic%20design%20white%20background%20emerald%20gradient&width=400&height=300&seq=act-eng-grammar-review&orientation=landscape'
  },

  // Sentence Structure (6 worksheets)
  {
    id: 'act-eng-structure-1',
    title: 'Sentence Fragments',
    description: 'Identify and correct incomplete sentences that lack a subject, verb, or complete thought',
    category: 'Sentence Structure',
    difficulty: 'Beginner',
    pages: 4,
    questions: 25,
    downloadUrl: 'https://readdy.ai/api/search-image?query=educational%20worksheet%20design%20showing%20sentence%20fragments%20with%20missing%20parts%20highlighted%20and%20complete%20sentences%20for%20comparison%20clean%20academic%20style%20white%20background%20teal%20accents&width=400&height=300&seq=act-eng-structure-1&orientation=landscape'
  },
  {
    id: 'act-eng-structure-2',
    title: 'Run-On Sentences and Fused Sentences',
    description: 'Practice identifying and correcting run-on sentences with proper punctuation or conjunctions',
    category: 'Sentence Structure',
    difficulty: 'Intermediate',
    pages: 4,
    questions: 25,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20worksheet%20header%20showing%20run-on%20sentences%20with%20two%20independent%20clauses%20incorrectly%20joined%20and%20correction%20methods%20clean%20minimalist%20design%20white%20background%20emerald%20accents&width=400&height=300&seq=act-eng-structure-2&orientation=landscape'
  },
  {
    id: 'act-eng-structure-3',
    title: 'Comma Splices',
    description: 'Learn to identify and fix comma splices using semicolons, periods, or coordinating conjunctions',
    category: 'Sentence Structure',
    difficulty: 'Intermediate',
    pages: 4,
    questions: 25,
    downloadUrl: 'https://readdy.ai/api/search-image?query=educational%20worksheet%20design%20featuring%20comma%20splice%20errors%20with%20two%20independent%20clauses%20joined%20by%20comma%20and%20correction%20options%20clean%20academic%20layout%20white%20background%20teal%20color%20scheme&width=400&height=300&seq=act-eng-structure-3&orientation=landscape'
  },
  {
    id: 'act-eng-structure-4',
    title: 'Coordination and Subordination',
    description: 'Master the use of coordinating and subordinating conjunctions to join clauses effectively',
    category: 'Sentence Structure',
    difficulty: 'Intermediate',
    pages: 4,
    questions: 30,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20worksheet%20header%20showing%20coordinating%20conjunctions%20FANBOYS%20and%20subordinating%20conjunctions%20connecting%20clauses%20clean%20academic%20design%20white%20background%20emerald%20and%20teal%20accents&width=400&height=300&seq=act-eng-structure-4&orientation=landscape'
  },
  {
    id: 'act-eng-structure-5',
    title: 'Parallel Structure',
    description: 'Practice maintaining parallel structure in lists, comparisons, and paired elements',
    category: 'Sentence Structure',
    difficulty: 'Advanced',
    pages: 4,
    questions: 30,
    downloadUrl: 'https://readdy.ai/api/search-image?query=educational%20worksheet%20design%20showing%20parallel%20structure%20with%20matching%20grammatical%20forms%20in%20lists%20and%20comparisons%20clean%20minimalist%20style%20white%20background%20teal%20and%20emerald%20color%20scheme&width=400&height=300&seq=act-eng-structure-5&orientation=landscape'
  },
  {
    id: 'act-eng-structure-review',
    title: 'Comprehensive Sentence Structure Review',
    description: 'Mixed practice covering fragments, run-ons, comma splices, coordination, and parallelism',
    category: 'Sentence Structure',
    difficulty: 'Advanced',
    pages: 6,
    questions: 40,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20comprehensive%20sentence%20structure%20review%20worksheet%20with%20multiple%20sentence%20types%20and%20correction%20methods%20clean%20academic%20design%20white%20background%20emerald%20gradient&width=400&height=300&seq=act-eng-structure-review&orientation=landscape'
  },

  // Rhetorical Skills (5 worksheets)
  {
    id: 'act-eng-rhetoric-1',
    title: 'Topic Development and Strategy',
    description: 'Practice adding, revising, or deleting material based on the passage purpose and focus',
    category: 'Rhetorical Skills',
    difficulty: 'Intermediate',
    pages: 5,
    questions: 30,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20educational%20worksheet%20design%20showing%20topic%20development%20with%20main%20ideas%20and%20supporting%20details%20organized%20in%20outline%20format%20clean%20academic%20style%20white%20background%20teal%20accents&width=400&height=300&seq=act-eng-rhetoric-1&orientation=landscape'
  },
  {
    id: 'act-eng-rhetoric-2',
    title: 'Organization and Logical Sequence',
    description: 'Master ordering sentences within paragraphs and paragraphs within passages logically',
    category: 'Rhetorical Skills',
    difficulty: 'Intermediate',
    pages: 5,
    questions: 30,
    downloadUrl: 'https://readdy.ai/api/search-image?query=educational%20worksheet%20header%20featuring%20paragraph%20organization%20with%20numbered%20sentences%20and%20arrows%20showing%20logical%20flow%20clean%20minimalist%20design%20white%20background%20emerald%20and%20teal%20color%20scheme&width=400&height=300&seq=act-eng-rhetoric-2&orientation=landscape'
  },
  {
    id: 'act-eng-rhetoric-3',
    title: 'Transitions and Coherence',
    description: 'Practice using effective transition words and phrases to connect ideas smoothly',
    category: 'Rhetorical Skills',
    difficulty: 'Intermediate',
    pages: 4,
    questions: 25,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20worksheet%20design%20showing%20transition%20words%20connecting%20paragraphs%20and%20sentences%20with%20arrows%20and%20flow%20indicators%20clean%20academic%20layout%20white%20background%20teal%20accents&width=400&height=300&seq=act-eng-rhetoric-3&orientation=landscape'
  },
  {
    id: 'act-eng-rhetoric-4',
    title: 'Unity and Focus',
    description: 'Learn to maintain consistent focus and eliminate irrelevant information from passages',
    category: 'Rhetorical Skills',
    difficulty: 'Advanced',
    pages: 5,
    questions: 30,
    downloadUrl: 'https://readdy.ai/api/search-image?query=educational%20worksheet%20header%20featuring%20unified%20paragraph%20with%20main%20topic%20and%20relevant%20supporting%20sentences%20highlighted%20clean%20minimalist%20academic%20design%20white%20background%20emerald%20accents&width=400&height=300&seq=act-eng-rhetoric-4&orientation=landscape'
  },
  {
    id: 'act-eng-rhetoric-review',
    title: 'Comprehensive Rhetorical Skills Review',
    description: 'Mixed practice covering topic development, organization, transitions, and unity',
    category: 'Rhetorical Skills',
    difficulty: 'Advanced',
    pages: 6,
    questions: 40,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20comprehensive%20rhetorical%20skills%20review%20worksheet%20with%20passage%20organization%20and%20development%20concepts%20clean%20academic%20design%20white%20background%20teal%20and%20emerald%20gradient&width=400&height=300&seq=act-eng-rhetoric-review&orientation=landscape'
  },

  // Word Choice (5 worksheets)
  {
    id: 'act-eng-word-1',
    title: 'Precise Word Choice and Diction',
    description: 'Practice selecting the most accurate and appropriate words for specific contexts',
    category: 'Word Choice',
    difficulty: 'Intermediate',
    pages: 4,
    questions: 25,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20educational%20worksheet%20design%20showing%20word%20choice%20options%20with%20precise%20versus%20vague%20words%20highlighted%20clean%20academic%20style%20white%20background%20emerald%20and%20teal%20accents&width=400&height=300&seq=act-eng-word-1&orientation=landscape'
  },
  {
    id: 'act-eng-word-2',
    title: 'Conciseness and Eliminating Wordiness',
    description: 'Master choosing the shortest, clearest option that conveys the intended meaning',
    category: 'Word Choice',
    difficulty: 'Intermediate',
    pages: 4,
    questions: 25,
    downloadUrl: 'https://readdy.ai/api/search-image?query=educational%20worksheet%20header%20featuring%20wordy%20sentences%20crossed%20out%20with%20concise%20versions%20below%20clean%20minimalist%20design%20white%20background%20teal%20color%20scheme%20academic%20style&width=400&height=300&seq=act-eng-word-2&orientation=landscape'
  },
  {
    id: 'act-eng-word-3',
    title: 'Eliminating Redundancy',
    description: 'Practice identifying and removing redundant words and phrases from sentences',
    category: 'Word Choice',
    difficulty: 'Beginner',
    pages: 3,
    questions: 20,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20worksheet%20design%20showing%20redundant%20phrases%20with%20unnecessary%20words%20highlighted%20and%20streamlined%20versions%20clean%20academic%20layout%20white%20background%20emerald%20accents&width=400&height=300&seq=act-eng-word-3&orientation=landscape'
  },
  {
    id: 'act-eng-word-4',
    title: 'Tone and Style Consistency',
    description: 'Learn to maintain consistent formality level and match the author established tone',
    category: 'Word Choice',
    difficulty: 'Advanced',
    pages: 4,
    questions: 25,
    downloadUrl: 'https://readdy.ai/api/search-image?query=educational%20worksheet%20header%20featuring%20formal%20and%20informal%20language%20examples%20with%20tone%20indicators%20clean%20minimalist%20academic%20design%20white%20background%20teal%20and%20emerald%20color%20scheme&width=400&height=300&seq=act-eng-word-4&orientation=landscape'
  },
  {
    id: 'act-eng-word-review',
    title: 'Comprehensive Word Choice Review',
    description: 'Mixed practice covering precision, conciseness, redundancy, and tone consistency',
    category: 'Word Choice',
    difficulty: 'Advanced',
    pages: 5,
    questions: 35,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20comprehensive%20word%20choice%20review%20worksheet%20with%20multiple%20diction%20and%20style%20concepts%20clean%20academic%20design%20white%20background%20emerald%20gradient&width=400&height=300&seq=act-eng-word-review&orientation=landscape'
  },

  // Full Practice (3 worksheets)
  {
    id: 'act-eng-practice-1',
    title: 'Full ACT English Practice Test 1',
    description: 'Complete 75-question practice test with 5 passages covering all ACT English skills',
    category: 'Full Practice',
    difficulty: 'Advanced',
    pages: 12,
    questions: 75,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20full-length%20ACT%20English%20practice%20test%20cover%20page%20with%20test%20booklet%20design%20featuring%2075%20questions%205%20passages%20clean%20academic%20style%20white%20background%20emerald%20and%20teal%20accents&width=400&height=300&seq=act-eng-practice-1&orientation=landscape'
  },
  {
    id: 'act-eng-practice-2',
    title: 'Full ACT English Practice Test 2',
    description: 'Complete 75-question practice test with 5 passages covering all ACT English skills',
    category: 'Full Practice',
    difficulty: 'Advanced',
    pages: 12,
    questions: 75,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20full-length%20ACT%20English%20practice%20test%20cover%20page%20design%20with%20official%20test%20format%2075%20questions%205%20passages%20clean%20academic%20layout%20white%20background%20teal%20and%20emerald%20color%20scheme&width=400&height=300&seq=act-eng-practice-2&orientation=landscape'
  },
  {
    id: 'act-eng-practice-3',
    title: 'Full ACT English Practice Test 3',
    description: 'Complete 75-question practice test with 5 passages covering all ACT English skills',
    category: 'Full Practice',
    difficulty: 'Advanced',
    pages: 12,
    questions: 75,
    downloadUrl: 'https://readdy.ai/api/search-image?query=professional%20full-length%20ACT%20English%20practice%20test%20booklet%20cover%20with%20comprehensive%20test%20format%2075%20questions%205%20passages%20clean%20minimalist%20academic%20design%20white%20background%20emerald%20gradient&width=400&height=300&seq=act-eng-practice-3&orientation=landscape'
  }
];

const categories = [
  'All Categories',
  'Punctuation',
  'Grammar & Usage',
  'Sentence Structure',
  'Rhetorical Skills',
  'Word Choice',
  'Full Practice'
];

const difficultyLevels = ['All Levels', 'Beginner', 'Intermediate', 'Advanced'];

const categoryColors: Record<string, string> = {
  'Punctuation': 'bg-emerald-100 text-emerald-700',
  'Grammar & Usage': 'bg-teal-100 text-teal-700',
  'Sentence Structure': 'bg-cyan-100 text-cyan-700',
  'Rhetorical Skills': 'bg-blue-100 text-blue-700',
  'Word Choice': 'bg-indigo-100 text-indigo-700',
  'Full Practice': 'bg-purple-100 text-purple-700'
};

export default function ACTEnglishWorksheetsPage() {
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [selectedDifficulty, setSelectedDifficulty] = useState('All Levels');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredWorksheets = useMemo(() => {
    return worksheets.filter(worksheet => {
      const matchesCategory = selectedCategory === 'All Categories' || worksheet.category === selectedCategory;
      const matchesDifficulty = selectedDifficulty === 'All Levels' || worksheet.difficulty === selectedDifficulty;
      const matchesSearch = searchQuery === '' || 
        worksheet.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        worksheet.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      return matchesCategory && matchesDifficulty && matchesSearch;
    });
  }, [selectedCategory, selectedDifficulty, searchQuery]);

  const getCategoryStats = () => {
    const stats: Record<string, number> = {};
    worksheets.forEach(w => {
      stats[w.category] = (stats[w.category] || 0) + 1;
    });
    return stats;
  };

  const categoryStats = getCategoryStats();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50 to-teal-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <i className="ri-file-text-line text-4xl"></i>
            <h1 className="text-4xl font-bold">ACT English Worksheets</h1>
          </div>
          <p className="text-xl text-emerald-50 mb-6 max-w-3xl">
            Browse 31 comprehensive worksheets covering all ACT English skills — from punctuation and grammar to rhetorical skills and word choice
          </p>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
              <i className="ri-file-list-3-line"></i>
              <span>31 Worksheets</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
              <i className="ri-question-line"></i>
              <span>900+ Practice Questions</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full">
              <i className="ri-timer-line"></i>
              <span>Timed Practice Tests</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <a
          href="/test-prep/act/english"
          className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-all group"
        >
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
            <i className="ri-book-open-line text-2xl text-white"></i>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">ACT English Curriculum</h3>
          <p className="text-slate-600 text-sm">Review all topics and practice questions</p>
        </a>

        <a
          href="/act-english-study-plan"
          className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-all group"
        >
          <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
            <i className="ri-calendar-check-line text-2xl text-white"></i>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">Study Plan</h3>
          <p className="text-slate-600 text-sm">Create a personalized prep schedule</p>
        </a>

        <a
          href="/act-english-progress"
          className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-all group"
        >
          <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
            <i className="ri-bar-chart-box-line text-2xl text-white"></i>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">Progress Tracker</h3>
          <p className="text-slate-600 text-sm">Track your mastery across all topics</p>
        </a>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 mb-8">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="md:col-span-3">
              <div className="relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
                <input
                  type="text"
                  placeholder="Search worksheets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat} {cat !== 'All Categories' && `(${categoryStats[cat] || 0})`}
                  </option>
                ))}
              </select>
            </div>

            {/* Difficulty Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Difficulty</label>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                {difficultyLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>

            {/* Results Count */}
            <div className="flex items-end">
              <div className="text-sm text-slate-600">
                Showing <span className="font-semibold text-emerald-600">{filteredWorksheets.length}</span> of {worksheets.length} worksheets
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Worksheets Grid */}
      <div className="max-w-7xl mx-auto px-6 pb-16">
        {filteredWorksheets.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <i className="ri-file-search-line text-6xl text-slate-300 mb-4"></i>
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No worksheets found</h3>
            <p className="text-slate-600">Try adjusting your filters or search query</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWorksheets.map(worksheet => (
              <div 
                key={worksheet.id}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group"
              >
                {/* Thumbnail */}
                <div className="relative h-48 bg-gradient-to-br from-emerald-100 to-teal-100 overflow-hidden">
                  <img 
                    src={worksheet.downloadUrl}
                    alt={worksheet.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-3 right-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${categoryColors[worksheet.category]}`}>
                      {worksheet.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="font-bold text-lg text-slate-800 mb-2 line-clamp-2 group-hover:text-emerald-600 transition-colors">
                    {worksheet.title}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                    {worksheet.description}
                  </p>

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-xs text-slate-500 mb-4">
                    <div className="flex items-center gap-1">
                      <i className="ri-file-list-line"></i>
                      <span>{worksheet.pages} pages</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <i className="ri-question-line"></i>
                      <span>{worksheet.questions} questions</span>
                    </div>
                  </div>

                  {/* Difficulty Badge */}
                  <div className="flex items-center justify-between mb-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      worksheet.difficulty === 'Beginner' ? 'bg-green-100 text-green-700' :
                      worksheet.difficulty === 'Intermediate' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {worksheet.difficulty}
                    </span>
                  </div>

                  {/* Download Button */}
                  <button className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-3 rounded-lg font-medium hover:from-emerald-700 hover:to-teal-700 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap">
                    <i className="ri-download-line"></i>
                    Download Worksheet
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}